import { styles } from "../experiment.styles";
import {
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  ActionButton,
  TooltipHost,
} from "@fluentui/react";
import { ExperimentType } from "../../Types";
import { getExpStudioPath } from "../../Shared/shared.utils";
import { useRef, useState } from "react";
import { ElxCopyTarget, IElxCopyTargetHandle } from "@elixir/components";
import useAppInsights from "../../Hooks/UseAppInsights";
import { NavType } from "../../Shared/AppInsights";
import { useAppContext } from "../../Contexts";

interface ExperimentOverviewProps {
  /**
   * The experiment data to display in the overview
   */
  experimentDetails: ExperimentType;
  /**
   * When true, indicates the experiment is archived
   */
  isArchived?: boolean;
}

/**
 * Displays a summary overview of an experiment including name, description,
 * and pull request link (if available). Shows "Not set" for missing fields.
 */
const ExperimentOverview: React.FC<ExperimentOverviewProps> = ({
  experimentDetails,
  isArchived = false,
}) => {
  const copyTargetRef = useRef<IElxCopyTargetHandle>(null);
  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("ExperimentOverview");

  const [toolTipText, setToolTipText] = useState<string>("Copy link");
  const expStudioUrl = getExpStudioPath(
    experimentDetails.__extension,
    experimentDetails.featureKey,
  );
  const { extensionName } = useAppContext();
  return (
    <Stack tokens={{ childrenGap: 5 }}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        className={styles.experimentOverview}
      >
        <Stack tokens={{ childrenGap: 5 }}>
          <Stack.Item align="start">
            <span className={styles.experimentOverviewText}>
              Extension name:{" "}
            </span>
            {extensionName || (
              <span className={styles.experimentOverviewNotSetText}>
                Not set
              </span>
            )}
          </Stack.Item>
          <Stack.Item align="start">
            <span className={styles.experimentOverviewText}>Name: </span>
            {experimentDetails?.featureName ||
              experimentDetails?.__filename || (
                <span className={styles.experimentOverviewNotSetText}>
                  Not set
                </span>
              )}
          </Stack.Item>
          <Stack
            horizontal
            tokens={{ childrenGap: 5 }}
            className={styles.experimentOverviewDescription}
          >
            <span className={styles.experimentOverviewText}>Description: </span>
            {experimentDetails?.featureDescription ? (
              <span>{experimentDetails.featureDescription}</span>
            ) : (
              <span className={styles.experimentOverviewNotSetText}>
                Not set
              </span>
            )}
          </Stack>
          <Stack.Item align="start">
            {experimentDetails?.prUrl ||
            (experimentDetails?.pr && experimentDetails.prUrl) ? (
              <Stack horizontal tokens={{ childrenGap: 5 }}>
                <span className={styles.experimentOverviewText}>
                  Pull Request:
                </span>
                <Link
                  href={experimentDetails.prUrl}
                  target="_blank"
                  underline
                  className={styles.experimentFormLink}
                  onClick={() => {
                    trackNavigationEvent({
                      destination: experimentDetails.prUrl!,
                      navType: NavType.External,
                      properties: { pr: experimentDetails.pr },
                    });
                  }}
                >
                  {experimentDetails.prUrl}
                </Link>
              </Stack>
            ) : null}
          </Stack.Item>
        </Stack>
        <Stack>
          <TooltipHost content={toolTipText}>
            <ActionButton
              iconProps={{ iconName: "Link" }}
              onClick={() => {
                copyTargetRef.current?.copy();
                setToolTipText("Copied link!");
                setTimeout(() => setToolTipText("Copy link"), 2000);
                trackOperationEvent({
                  operation: "CopyLink",
                  function: "Share.onClick",
                  properties: { expStudioUrl },
                });
              }}
            >
              Share
            </ActionButton>
          </TooltipHost>
          <ElxCopyTarget ref={copyTargetRef} textToCopy={expStudioUrl} />
        </Stack>
      </Stack>
      {isArchived && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Archived experiments are read-only and cannot be modified. If changes
          are necessary, experiment will need to be unarchived first.
        </MessageBar>
      )}
    </Stack>
  );
};

export default ExperimentOverview;
